import { gql } from "@apollo/client";

export const CUSTOM_FIELD_DEFINITION_FRAGMENT = gql`
  fragment CustomFieldDefinitionFields on CustomFieldDefinitionModel {
    id
    objectType
    key
    name
    description
    type
    required
    unique
    order
    visibility
    editability
    options {
      label
      value
    }
    defaultValue
    validationRegex
    createdAt
    updatedAt
  }
`;

export const CUSTOM_FIELD_WITH_VALUE_FRAGMENT = gql`
  fragment CustomFieldWithValueFields on CustomFieldWithValueModel {
    definition {
      ...CustomFieldDefinitionFields
    }
    value
  }
  ${CUSTOM_FIELD_DEFINITION_FRAGMENT}
`;

export const OBJECT_METADATA_FRAGMENT = gql`
  fragment ObjectMetadataFields on ObjectMetadata {
    key
    value
    type
    visibility
    objectType
    objectId
    createdAt
    updatedAt
  }
`;

// Enums matching the backend
export enum CustomFieldType {
  STRING = "STRING",
  NUMBER = "NUMBER",
  BOOLEAN = "BOOLEAN",
  DATE = "DATE",
  EMAIL = "EMAIL",
  PHONE = "PHONE",
  URL = "URL",
  COUNTRY = "COUNTRY",
  SELECT = "SELECT",
  MULTI_SELECT = "MULTI_SELECT",
  IMAGE = "IMAGE",
}

export enum PropertyVisibility {
  PUBLIC = "PUBLIC",
  OWNER = "OWNER",
  WITH_PERMISSION = "WITH_PERMISSION",
  OWNER_OR_PERMISSION = "OWNER_OR_PERMISSION",
}

export enum PropertyEditability {
  ALWAYS = "ALWAYS",
  ONE_TIME = "ONE_TIME",
  WITH_PERMISSION = "WITH_PERMISSION",
}

export enum MetadataType {
  STRING = "STRING",
  BOOLEAN = "BOOLEAN",
  IMAGE_URL = "IMAGE_URL",
  URL = "URL",
}

export enum MetadataVisibility {
  PUBLIC = "PUBLIC",
  PRIVATE = "PRIVATE",
}

export const CUSTOM_FIELD_TYPE_OPTIONS = [
  { label: "String", value: CustomFieldType.STRING },
  { label: "Number", value: CustomFieldType.NUMBER },
  { label: "Boolean", value: CustomFieldType.BOOLEAN },
  { label: "Date", value: CustomFieldType.DATE },
  { label: "Email", value: CustomFieldType.EMAIL },
  { label: "Phone", value: CustomFieldType.PHONE },
  { label: "URL", value: CustomFieldType.URL },
  { label: "Country", value: CustomFieldType.COUNTRY },
  { label: "Select", value: CustomFieldType.SELECT },
  { label: "Multi Select", value: CustomFieldType.MULTI_SELECT },
  { label: "Image", value: CustomFieldType.IMAGE },
];

export const VISIBILITY_OPTIONS = [
  { label: "Public", value: PropertyVisibility.PUBLIC },
  { label: "Owner only", value: PropertyVisibility.OWNER },
  { label: "With Permission", value: PropertyVisibility.WITH_PERMISSION },
  {
    label: "Owner or Permission",
    value: PropertyVisibility.OWNER_OR_PERMISSION,
  },
];

export const EDITABILITY_OPTIONS = [
  { label: "Always (owner or permission)", value: PropertyEditability.ALWAYS },
  { label: "One Time", value: PropertyEditability.ONE_TIME },
  { label: "With Permission", value: PropertyEditability.WITH_PERMISSION },
];

export const METADATA_TYPE_OPTIONS = [
  { label: "String", value: MetadataType.STRING },
  { label: "Boolean", value: MetadataType.BOOLEAN },
  { label: "Image URL", value: MetadataType.IMAGE_URL },
  { label: "URL", value: MetadataType.URL },
];

export const METADATA_VISIBILITY_OPTIONS = [
  { label: "Public", value: MetadataVisibility.PUBLIC },
  { label: "Private", value: MetadataVisibility.PRIVATE },
];

// Shared interfaces
export interface CustomFieldDefinition {
  id: string;
  objectType: string;
  key: string;
  name: string;
  description?: string;
  type: CustomFieldType;
  required: boolean;
  unique: boolean;
  order: number;
  visibility: PropertyVisibility;
  editability: PropertyEditability;
  options?: { label: string; value: string }[];
  defaultValue?: string;
  validationRegex?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomFieldWithValue {
  definition: CustomFieldDefinition;
  value?: string;
}

export interface ObjectMetadata {
  key: string;
  value: string;
  type: MetadataType;
  visibility: MetadataVisibility;
  objectType: string;
  objectId: string;
  createdAt: string;
  updatedAt: string;
}
